import * as React from "react";
import {
  Building2,
  Check,
  ChevronsUpDown,
  GalleryVerticalEnd,
  Loader2,
} from "lucide-react";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate } from "react-router-dom";

import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  useSidebar,
} from "@/components/ui/sidebar";
import { Skeleton } from "@/components/ui/skeleton";
import { useProfile } from "@/context/ProfileContext";
import { useTeamScope } from "@/context/TeamScopeContext";
import { useSalesTeams } from "@/hooks/queries/use-sales-teams";
import { useToast } from "@/hooks/use-toast";
import { cn } from "@/lib/utils";

/**
 * The 44px organisation + scope row for the sidebar (redesign 1c). Merges the
 * old two stacked controls — the business switcher (which business) and the
 * team-scope switcher (which team scope within it) — into one row: an org tile,
 * the org name, the active team scope beneath it, and a single dropdown that
 * switches both.
 */
export function OrgScopeSwitcher({
  orgName,
  orgLogoUrl,
  isLoading = false,
  variant = "sidebar",
}: {
  orgName: string;
  orgLogoUrl?: string;
  isLoading?: boolean;
  /**
   * `sidebar` is the 44 px row in the phone sheet; `chip` is the compact
   * control in the desktop top bar (the sketch's scope switch), which
   * renders nothing when there is nothing to switch.
   */
  variant?: "sidebar" | "chip";
}) {
  const { isMobile } = useSidebar();
  const chip = variant === "chip";
  const { t } = useTranslation("common");
  const { t: tNav } = useTranslation("nav");
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const { toast } = useToast();
  const orgGroupId = React.useId();
  const teamGroupId = React.useId();
  const {
    memberships,
    activeBusinessId,
    switchBusiness,
    businessProfile,
    memberAccessLevel,
  } = useProfile();
  const businessId = businessProfile?.uid ?? "";
  const { activeTeamId, setActiveTeamId } = useTeamScope();
  // Report-only accounts (GRA-96) have no team data; don't ask for it.
  const teamsQuery = useSalesTeams(
    memberAccessLevel === "none" ? undefined : businessId || undefined,
  );
  const teams = teamsQuery.data ?? [];
  const canSeeAll = memberAccessLevel === "full";
  const [switching, setSwitching] = React.useState(false);

  const activeTeam = teams.find((tm) => tm.id === activeTeamId) ?? null;
  const scopeLabel = activeTeam
    ? activeTeam.name
    : canSeeAll
      ? tNav("teamScope.allTeams")
      : (teams[0]?.name ?? tNav("teamScope.allTeams"));

  const canSwitchBusiness = memberships.length > 1;
  const hasTeams = teams.length > 0;

  if (isLoading) {
    if (chip) return <Skeleton className="h-10 w-[180px] rounded-g-md" />;
    return (
      <SidebarMenu>
        <SidebarMenuItem>
          <SidebarMenuButton size="lg" className="pointer-events-none">
            <Skeleton className="size-7 rounded-lg" />
            <div className="grid flex-1 gap-1.5">
              <Skeleton className="h-3.5 w-24" />
              <Skeleton className="h-3 w-16" />
            </div>
          </SidebarMenuButton>
        </SidebarMenuItem>
      </SidebarMenu>
    );
  }

  // A plain element rather than an inline component: defining a component
  // inside render gives React a new type every render, which unmounts and
  // re-requests the logo <img> on every re-render.
  const orgTile = orgLogoUrl ? (
    <img
      src={orgLogoUrl}
      alt={orgName}
      className="size-7 shrink-0 rounded-lg object-cover"
    />
  ) : (
    <div className="flex size-7 shrink-0 items-center justify-center rounded-lg bg-primary text-primary-foreground">
      <GalleryVerticalEnd className="size-4" />
    </div>
  );

  const rowLabel = (
    <>
      {orgTile}
      <div className="grid flex-1 text-left leading-tight">
        <span className="truncate text-sm font-semibold">{orgName}</span>
        <span className="truncate text-[11.5px] text-muted-foreground">
          {switching
            ? t("companySwitcher.switching")
            : hasTeams
              ? scopeLabel
              : t("workspace")}
        </span>
      </div>
      {switching ? (
        <Loader2
          className="ml-auto size-4 shrink-0 animate-spin text-muted-foreground"
          aria-hidden="true"
        />
      ) : (
        <ChevronsUpDown
          className="ml-auto size-4 shrink-0 text-muted-foreground"
          aria-hidden="true"
        />
      )}
    </>
  );

  // Nothing to switch → a static, non-interactive branding header in the
  // sheet, and no control at all in the top bar.
  if (!canSwitchBusiness && !hasTeams) {
    if (chip) return null;
    return (
      <SidebarMenu>
        <SidebarMenuItem>
          <SidebarMenuButton size="lg" className="pointer-events-none">
            {orgTile}
            <div className="grid flex-1 text-left leading-tight">
              <span className="truncate text-sm font-semibold">{orgName}</span>
              <span className="truncate text-[11.5px] text-muted-foreground">
                {t("workspace")}
              </span>
            </div>
          </SidebarMenuButton>
        </SidebarMenuItem>
      </SidebarMenu>
    );
  }

  // The menu closes on select like any other pick; the row itself carries
  // the in-flight state (spinner + "Switching…" + disabled trigger) so the
  // user sees the round trip instead of a sidebar that appears to ignore
  // the click, and a failed switch is reported rather than swallowed.
  const selectBusiness = async (id: string) => {
    if (id === activeBusinessId || switching) return;
    setSwitching(true);
    try {
      await switchBusiness(id);
      // Reset to the home view so we never linger on a record from the
      // previously-active business.
      navigate("/");
    } catch (error) {
      console.error("Failed to switch business", error);
      toast({
        title: t("companySwitcher.switchError"),
        variant: "destructive",
      });
    } finally {
      setSwitching(false);
    }
  };

  const selectTeam = (id: string | null) => {
    setActiveTeamId(id);
    // Performance, Pulse and Tests already follow the active team, so
    // switching there just re-renders in place rather than yanking you into a
    // roster.
    if (/^\/coach\/(teams\/tests|performance(\/pulse)?)$/.test(pathname))
      return;
    navigate(id ? `/coach/teams/${id}` : "/coach/teams");
  };

  const trigger = chip ? (
    <button
      type="button"
      disabled={switching}
      aria-busy={switching || undefined}
      data-testid="scope-chip"
      className={cn(
        "inline-flex h-10 max-w-[300px] items-center gap-2 rounded-g-md border border-g-line bg-g-bg-card px-3 text-[13px] font-semibold text-g-ink-700 transition-colors duration-150 ease-out",
        "hover:bg-g-primary-wash hover:text-g-ink-900 data-[state=open]:bg-g-primary-wash",
        "focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-g-ring",
        "disabled:cursor-wait disabled:opacity-70",
      )}
    >
      <Building2
        className="size-4 shrink-0 text-g-ink-400"
        aria-hidden="true"
      />
      <span className="min-w-0 truncate">
        {canSwitchBusiness ? orgName : scopeLabel}
      </span>
      {canSwitchBusiness && hasTeams ? (
        <span className="min-w-0 truncate font-medium text-g-ink-400">
          · {scopeLabel}
        </span>
      ) : null}
      {switching ? (
        <Loader2
          className="size-4 shrink-0 animate-spin text-g-ink-400"
          aria-hidden="true"
        />
      ) : (
        <ChevronsUpDown
          className="size-4 shrink-0 text-g-ink-400"
          aria-hidden="true"
        />
      )}
    </button>
  ) : (
    <SidebarMenuButton
      size="lg"
      disabled={switching}
      aria-busy={switching || undefined}
      className="data-[state=open]:bg-sidebar-accent data-[state=open]:text-sidebar-accent-foreground"
    >
      {rowLabel}
    </SidebarMenuButton>
  );

  const menu = (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>{trigger}</DropdownMenuTrigger>
      <DropdownMenuContent
        className={cn(
          "min-w-60 rounded-lg",
          !chip && "w-[--radix-dropdown-menu-trigger-width]",
        )}
        side={chip || isMobile ? "bottom" : "right"}
        align={chip ? "end" : "start"}
        sideOffset={4}
      >
        {canSwitchBusiness ? (
          <DropdownMenuGroup aria-labelledby={orgGroupId}>
            <DropdownMenuLabel
              id={orgGroupId}
              className="text-xs text-muted-foreground"
            >
              {t("companySwitcher.switchTo")}
            </DropdownMenuLabel>
            {memberships.map((m) => {
              const isActive = m.businessId === activeBusinessId;
              return (
                <DropdownMenuItem
                  key={m.businessId}
                  role="menuitemradio"
                  aria-checked={isActive}
                  disabled={switching}
                  onSelect={() => void selectBusiness(m.businessId)}
                  className="gap-2"
                >
                  <div
                    className="flex size-6 items-center justify-center rounded-md border"
                    aria-hidden="true"
                  >
                    <Building2 className="size-3.5 shrink-0" />
                  </div>
                  <span className="truncate">{m.businessName}</span>
                  {isActive && (
                    <Check className="ml-auto size-4" aria-hidden="true" />
                  )}
                </DropdownMenuItem>
              );
            })}
          </DropdownMenuGroup>
        ) : null}

        {canSwitchBusiness && hasTeams ? <DropdownMenuSeparator /> : null}

        {hasTeams ? (
          <DropdownMenuGroup aria-labelledby={teamGroupId}>
            <DropdownMenuLabel
              id={teamGroupId}
              className="text-xs text-muted-foreground"
            >
              {tNav("teamScope.label")}
            </DropdownMenuLabel>
            {canSeeAll ? (
              <DropdownMenuItem
                role="menuitemradio"
                aria-checked={activeTeamId === null}
                className="gap-2"
                onSelect={() => selectTeam(null)}
              >
                <span className="truncate">{tNav("teamScope.allTeams")}</span>
                {activeTeamId === null && (
                  <Check
                    className="ml-auto size-4 shrink-0"
                    aria-hidden="true"
                  />
                )}
              </DropdownMenuItem>
            ) : null}
            {teams.map((tm) => (
              <DropdownMenuItem
                key={tm.id}
                role="menuitemradio"
                aria-checked={activeTeamId === tm.id}
                className="gap-2"
                onSelect={() => selectTeam(tm.id)}
              >
                <span className="truncate">{tm.name}</span>
                {activeTeamId === tm.id && (
                  <Check
                    className="ml-auto size-4 shrink-0"
                    aria-hidden="true"
                  />
                )}
              </DropdownMenuItem>
            ))}
          </DropdownMenuGroup>
        ) : null}
      </DropdownMenuContent>
    </DropdownMenu>
  );

  if (chip) return menu;
  return (
    <SidebarMenu>
      <SidebarMenuItem>{menu}</SidebarMenuItem>
    </SidebarMenu>
  );
}
